/**
 * 📊 useAffiliateDashboard Hook
 * Provides dashboard data with loading/error states
 */

import { useState, useEffect, useCallback } from 'react';
import {
  MOCK_KPI_DATA,
  MOCK_QUICK_STATS,
  MOCK_KONSTANTLEITER,
  MOCK_ACTIVITIES,
  MOCK_REFERRAL_LINK,
  MOCK_EARNINGS_CHART,
  type Activity,
} from '@/lib/affiliate-mock-data';
import type { EarningsDataPoint } from '@/components/affiliate/charts/EarningsChart';

// ============================================
// TYPES
// ============================================

interface KPIData {
  totalEarnings: number;
  monthlyEarnings: number;
  activeRestaurants: number;
  conversionRate: number;
}

interface QuickStat {
  label: string;
  value: string | number;
}

interface LevelProgress {
  currentLevel: number;
  currentLevelName: string;
  progress: number;
  pointsToNext: number;
}

interface DashboardData {
  kpis: KPIData;
  quickStats: QuickStat[];
  levelProgress: LevelProgress;
  activities: Activity[];
  referralLink: string;
  earningsChart: EarningsDataPoint[];
}

interface UseDashboardReturn {
  data: DashboardData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  copyReferralLink: () => Promise<boolean>;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

export function useAffiliateDashboard(): UseDashboardReturn {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // In production, this would be an API call
      setData({
        kpis: MOCK_KPI_DATA,
        quickStats: MOCK_QUICK_STATS,
        levelProgress: MOCK_KONSTANTLEITER,
        activities: MOCK_ACTIVITIES,
        referralLink: MOCK_REFERRAL_LINK,
        earningsChart: MOCK_EARNINGS_CHART,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch dashboard data'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyReferralLink = useCallback(async (): Promise<boolean> => {
    if (!data?.referralLink) return false;
    
    try {
      await navigator.clipboard.writeText(`https://${data.referralLink}`);
      return true;
    } catch {
      return false;
    }
  }, [data?.referralLink]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    copyReferralLink,
  };
}

export default useAffiliateDashboard;
